// reflectionEngagement.ts — anti-skip helpers for reflection steps. Pure.
//
// Reflections land better when sat with. We measure FOREGROUND dwell (page load →
// Next tap, paused while the app is backgrounded) and lean in gently when it's
// rushed — always offering help, never disapproval. We NEVER show or imply the
// time, and "I'm doing these my own way" durably silences the nudges.

/** Below this dwell (seconds) a soft nudge may appear; also the bar for "practised". */
export const REFLECTION_SOFT_NUDGE_UNDER_S = 60;
/** An advance faster than this (seconds) counts as a "skip" toward escalation. */
export const REFLECTION_SKIP_FLOOR_S = 15;
/** Escalate to the warmer prompt when ≥ this many of the recent window are skips. */
export const ESCALATE_COUNT = 3;
/** The recent-reflections window escalation looks back over. */
export const ESCALATE_WINDOW = 5;

/** Push a skip flag onto the window, keeping only the last ESCALATE_WINDOW. */
export function recordSkip(window: boolean[], underFloor: boolean): boolean[] {
  return [...window, underFloor].slice(-ESCALATE_WINDOW);
}

/** True when the recent window holds enough skips to warrant the warmer prompt. */
export function shouldEscalate(window: boolean[]): boolean {
  return window.slice(-ESCALATE_WINDOW).filter(Boolean).length >= ESCALATE_COUNT;
}

export type ReflectionNudge = 'none' | 'soft' | 'escalate';

/**
 * Which nudge (if any) to show on an advance.
 * @param seconds              foreground dwell on this reflection
 * @param skipsAfter           the skip window AFTER recording this advance
 * @param ownWay               user has durably opted out
 * @param softShownThisSession a soft nudge has already shown this app session
 */
export function nudgeFor(
  seconds: number,
  skipsAfter: boolean[],
  ownWay: boolean,
  softShownThisSession: boolean,
): ReflectionNudge {
  if (ownWay) return 'none';
  if (seconds >= REFLECTION_SOFT_NUDGE_UNDER_S) return 'none';
  if (seconds < REFLECTION_SKIP_FLOOR_S && shouldEscalate(skipsAfter)) return 'escalate';
  if (!softShownThisSession) return 'soft';
  return 'none';
}
